import { SeededRandom } from '../core/Random';
import type { OwnedUnit, SaveData, TraitRoll } from '../core/types';
import { units } from '../data/content';
import { WalletService } from '../economy/WalletService';

const traitPool: TraitRoll[] = [
  { id: 'fierce', name: 'Fierce', tier: 'Normal', stat: 'attack', value: 0.05 },
  { id: 'swift', name: 'Swift', tier: 'Advanced', stat: 'speed', value: 5 },
  { id: 'guardian', name: 'Guardian', tier: 'Elite', stat: 'defense', value: 0.14 },
  { id: 'executioner', name: 'Executioner', tier: 'Supreme', stat: 'critRate', value: 0.16 },
  { id: 'lifebloom', name: 'Lifebloom', tier: 'Elite', stat: 'healingBonus', value: 0.18 },
  { id: 'overcharge', name: 'Overcharge', tier: 'Supreme', stat: 'energyRegen', value: 0.2 },
  { id: 'celestial-monarch', name: 'Celestial Monarch', tier: 'Celestial', stat: 'attack', value: 0.22 },
];

export class ProgressionService {
  constructor(private readonly save: SaveData, private readonly rng = new SeededRandom()) {}

  levelUp(instanceId: string, levels = 1): OwnedUnit {
    const unit = this.owned(instanceId);
    const max = 60 + unit.ascension * 10;
    const gain = Math.min(Math.max(0, Math.floor(levels)), max - unit.level);
    if (gain <= 0) throw new Error('Unit is at the current level cap');
    const cost = gain * (220 + unit.level * 32);
    const materials = gain;
    const wallet = new WalletService(this.save.wallet);
    wallet.spend({ gold: cost, expCrystals: materials });
    unit.level += gain;
    return unit;
  }

  ascend(instanceId: string): OwnedUnit {
    const unit = this.owned(instanceId);
    if (unit.ascension >= 4) throw new Error('Maximum ascension reached');
    const requiredLevel = 60 + unit.ascension * 10;
    if (unit.level < requiredLevel) throw new Error(`Reach level ${requiredLevel} first`);
    new WalletService(this.save.wallet).spend({ gold: 12000 * (unit.ascension + 1), ascensionCrystals: 4 + unit.ascension * 3 });
    unit.ascension += 1;
    return unit;
  }

  awaken(instanceId: string): OwnedUnit {
    const unit = this.owned(instanceId);
    if (unit.awakening >= 5) throw new Error('Maximum awakening reached');
    const cost = 40 + unit.awakening * 20;
    if (unit.shards < cost) throw new Error('Not enough unit shards');
    unit.shards -= cost; unit.awakening += 1;
    return unit;
  }

  evolve(instanceId: string): OwnedUnit {
    const owned = this.owned(instanceId); const definition = units.find((unit) => unit.id === owned.definitionId);
    if (!definition?.evolutionId) throw new Error('This unit has no evolution');
    if (owned.evolved) throw new Error('Unit is already evolved');
    if (owned.ascension < 2) throw new Error('Ascension 2 is required');
    new WalletService(this.save.wallet).spend({ gold: 30000, evolutionMaterials: 3 });
    owned.evolved = true; return owned;
  }

  upgradeRelic(relicId: string): void {
    const relic = this.save.relics.find((item) => item.id === relicId); if (!relic) throw new Error('Relic not found');
    if (relic.level >= 20) throw new Error('Relic is at maximum level');
    new WalletService(this.save.wallet).spend({ relicDust: relic.level * 20 }); relic.level += 1; relic.value += relic.mainStat === 'speed' ? 1 : 0.012;
  }

  autoEquip(instanceId: string): void {
    const owned = this.owned(instanceId); const definition = units.find((unit) => unit.id === owned.definitionId)!;
    const preferred = definition.role === 'Tank' ? ['Vanguard', 'Glacier'] : definition.role === 'Healer' ? ['Oracle', 'Tempest'] : ['Hunter', 'Inferno', 'Voidwalker'];
    for (const slot of ['weapon', 'armor', 'core'] as const) {
      const candidate = [...this.save.relics].filter((item) => item.slot === slot && !item.locked && (!item.equippedTo || item.equippedTo === instanceId)).sort((a, b) => (preferred.indexOf(a.set) === -1 ? 99 : preferred.indexOf(a.set)) - (preferred.indexOf(b.set) === -1 ? 99 : preferred.indexOf(b.set)) || b.level - a.level)[0];
      if (!candidate) continue; const oldId = owned.relicSlots[slot]; if (oldId) { const old = this.save.relics.find((item) => item.id === oldId); if (old) delete old.equippedTo; }
      candidate.equippedTo = instanceId; owned.relicSlots[slot] = candidate.id;
    }
  }

  rollTrait(instanceId: string): TraitRoll {
    this.owned(instanceId);
    new WalletService(this.save.wallet).spend({ traitTokens: 1 });
    const weights = [50, 27, 14, 7.5, 1.5];
    const tiers: TraitRoll['tier'][] = ['Normal', 'Advanced', 'Elite', 'Supreme', 'Celestial'];
    const tier = this.rng.weighted(tiers.map((value, i) => ({ value, weight: weights[i]! })));
    const matching = traitPool.filter((trait) => trait.tier === tier);
    const base = matching.length ? this.rng.pick(matching) : this.rng.pick(traitPool);
    return { ...base, id: `${base.id}-${Date.now()}` };
  }

  acceptTrait(instanceId: string, roll: TraitRoll): void { this.owned(instanceId).trait = { ...roll }; }

  autoBuild(): string[] {
    const scored = [...this.save.units].sort((a, b) => this.power(b) - this.power(a));
    const selected: OwnedUnit[] = [];
    const add = (predicate: (unit: OwnedUnit) => boolean) => {
      const found = scored.find((unit) => !selected.includes(unit) && predicate(unit));
      if (found) selected.push(found);
    };
    add((owned) => units.find((unit) => unit.id === owned.definitionId)?.role === 'Tank');
    add((owned) => ['Healer', 'Buffer', 'Energy Support'].includes(units.find((unit) => unit.id === owned.definitionId)?.role ?? ''));
    scored.forEach((owned) => { if (selected.length < 5 && !selected.includes(owned)) selected.push(owned); });
    return selected.slice(0, 5).map((unit) => unit.instanceId);
  }

  power(owned: OwnedUnit): number {
    const def = units.find((unit) => unit.id === owned.definitionId);
    if (!def) return 0;
    const relicPower = Object.values(owned.relicSlots).reduce((sum, id) => sum + (this.save.relics.find((item) => item.id === id)?.level ?? 0) * 45, 0);
    return Math.round((def.baseStats.hp * 0.18 + def.baseStats.attack * 4 + def.baseStats.defense * 2.5 + def.baseStats.speed * 3) * (1 + (owned.level - 1) * 0.035 + owned.ascension * 0.2 + owned.awakening * 0.12 + (owned.evolved ? 0.28 : 0)) + relicPower);
  }

  private owned(instanceId: string): OwnedUnit {
    const unit = this.save.units.find((item) => item.instanceId === instanceId);
    if (!unit) throw new Error('Owned unit not found');
    return unit;
  }
}
